import { reactive, computed, watch } from 'vue'
import type { IUiFormBase, IUiFormProducts, VariantRules } from '@/domains/direct-sales/types/form.types'
import { EUIFormVariant, EUiFormCountry } from '@/domains/direct-sales/const/form.const'
import { useConsent } from '@/domains/direct-sales/composables/useConsent'
import useVuelidate from '@vuelidate/core'
import { useText } from '@/components/Inputs/Composables/validators/useText/useText'
import { useEmail } from '@/components/Inputs/Composables/validators/useEmail/useEmail'
import { useSelect } from '@/components/Inputs/Composables/validators/useSelect/useSelect'
import { useMultiSelect } from '@/components/Inputs/Composables/validators/useMultiSelect/useMultiSelect'
import { usePhone } from '@/components/Inputs/Composables/validators/usePhone/usePhone'
import { useNip } from '@/components/Inputs/Composables/validators/useNip/useNip'
import { ECountry } from '@/components/Inputs/Composables/validators/useNip/useNip.types'
import { EPHONE_CODES } from '@/components/Inputs/Composables/validators/usePhone/usePhone.types'
import { useCheckbox } from '@/components/Inputs/Composables/validators/useCheckbox/useCheckbox'

type FormByVariant<V extends EUIFormVariant> =
  V extends typeof EUIFormVariant.Products ? IUiFormProducts : IUiFormBase

const BASE_FORM_INITIAL_STATE: IUiFormBase = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyName: '',
  nip: '',
  companySize: {
    value: '',
    text: '',
  },
  description: '',
  terms: false,
  country: {
    value: '',
    text: '',
  },
}

const PRODUCTS_FORM_INITIAL_STATE: IUiFormProducts = {
  ...BASE_FORM_INITIAL_STATE,
  products: [],
}

const VARIANT_RULES: Record<EUIFormVariant, VariantRules> = {
  [EUIFormVariant.Base]: () => ({}),
  [EUIFormVariant.Products]: (t) => useMultiSelect('products', t),
}

export default function useUiForm<V extends EUIFormVariant>(variant: V) {
  const { t } = useI18n()
  const initialState = variant === EUIFormVariant.Products
    ? { ...PRODUCTS_FORM_INITIAL_STATE }
    : { ...BASE_FORM_INITIAL_STATE }

  const form = reactive(initialState) as FormByVariant<V>
  const { consentForm, consents, setAllConsents, resetConsent } = useConsent()

  const phoneCountry = computed(() =>
    form.country?.value === EUiFormCountry.POLAND ? EPHONE_CODES.POLAND : EPHONE_CODES.OTHER,
  )

  const phoneMessage = () =>
    form.country?.value === EUiFormCountry.POLAND
      ? t('formValidatorMessages.phoneNumber.PL_DIRECT_SALES')
      : t('formValidatorMessages.phoneNumber.OTHER')
  
  const $v = useVuelidate({
    ...useText('firstName', t, { length: 40, required: true }),
    ...useText('lastName', t, { length: 80, required: true }),
    ...useEmail('email', t),
    ...useSelect('country', t, { required: true }),
    ...usePhone('phone', phoneCountry, t, phoneMessage, { prefixRequired: true }),
    ...useText('companyName', t, { length: 255, required: true }),
    ...useNip('nip', ECountry.POLAND, t),
    ...useSelect('companySize', t, { required: true }),
    ...useText('description', t, { length: 32000, required: false }),
    ...useCheckbox('terms', t, { required: true }),
    ...VARIANT_RULES[variant](t),
  }, form)

  watch(() => form.country.value, (country) => {
    if (country === EUiFormCountry.POLAND && !form.phone.startsWith('+48')) {
      form.phone = `+48${form.phone.replace('+', '')}`
      return
    }

    if (country === EUiFormCountry.OTHER && (form.phone.startsWith('+48') || !form.phone.startsWith('+'))) {
      form.phone = '+'
    }
  })

  const resetForm = () => {
    Object.assign(form, initialState)
    resetConsent()
    $v.value.$reset()
  }

  return {
    $v,
    consentForm,
    consents,
    setAllConsents,
    form,
    resetForm,
  }
}
